import React, { useEffect, useMemo, useState } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FaPlus, FaSearch, FaTags, FaTrash, FaEdit } from 'react-icons/fa';
import apiClient from '../../../shared/utils/apiConfig';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';

const UNIT_TYPES = [
  'pack','size','weight','bottle','tube','box','piece','jar','set'
];

const AdminUnits = () => {
  const { setTitle } = useTitle();
  const { isSuperAdmin } = usePermissions();

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: '', category_id: '' });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, name: '', type: '', category_id: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => { setTitle('الوحدات'); }, [setTitle]);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/units/get_all');
      // يدعم كل الأشكال المحتملة
      const list =
        Array.isArray(res.data?.result) ? res.data.result :
        res.data?.result?.data ||
        res.data?.data ||
        res.data || [];
      setUnits(Array.isArray(list) ? list : []);
    } catch (e) {
      showErrorToast(e.response?.data?.message || 'فشل في جلب الوحدات');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await apiClient.get('/admin/categories');
      const list = res.data?.result?.data || res.data?.data || [];
      setCategories(Array.isArray(list) ? list : []);
    } catch (e) {
      // صامت
    }
  };

  // لم نعد نحتاج خريطة الفئات للعرض، سنستخدم category_name القادم من الـ API

  const filteredUnits = useMemo(() => {
    if (!search.trim()) return units;
    const q = search.trim().toLowerCase();
    return units.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.type || '').toLowerCase().includes(q)
    );
  }, [units, search]);

  const openAdd = async () => {
    setForm({ name: '', type: '', category_id: '' });
    // جلب الفئات عند فتح مودال الإضافة فقط
    await fetchCategories();
    setAddModalOpen(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!form.name || !form.type || !form.category_id) {
      showErrorToast('يرجى إدخال جميع الحقول');
      return;
    }
    try {
      const payload = {
        name: form.name,
        type: form.type,
        category_id: Number(form.category_id)
      };
      await apiClient.post('/units/add_new', payload);
      showSuccessToast('تمت إضافة الوحدة بنجاح');
      setAddModalOpen(false);
      fetchUnits();
    } catch (e) {
      showErrorToast(e.response?.data?.message || 'فشل في إضافة الوحدة');
    }
  };

  const handleDeleteUnit = (unit) => {
    if (!unit?.id) return;
    setDeleteTarget(unit);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUnit = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeletingId(deleteTarget.id);
      await apiClient.delete(`/units/delete_one/${deleteTarget.id}`);
      showSuccessToast('تم حذف الوحدة بنجاح');
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      fetchUnits();
    } catch (e) {
      showErrorToast(e.response?.data?.message || 'فشل في حذف الوحدة');
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = async (unit) => {
    if (!unit) return;
    await fetchCategories();
    setEditForm({ id: unit.id, name: unit.name || '', type: unit.type || '', category_id: unit.category_id || '' });
    setEditModalOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.id) return;
    if (!editForm.name || !editForm.type || !editForm.category_id) {
      showErrorToast('يرجى إدخال جميع الحقول');
      return;
    }
    try {
      setSavingEdit(true);
      const payload = {
        name: editForm.name,
        type: editForm.type,
        category_id: Number(editForm.category_id)
      };
      await apiClient.post(`/units/edit_one/${editForm.id}`, payload);
      showSuccessToast('تم تعديل الوحدة بنجاح');
      setEditModalOpen(false);
      fetchUnits();
    } catch (e) {
      showErrorToast(e.response?.data?.message || 'فشل في تعديل الوحدة');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="main_content">
      <div className="dashboard-container">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">الوحدات</h4>
          <button className="btn text-white" style={{ backgroundColor: 'var(--main-color)' }} onClick={openAdd}>
            <FaPlus className="ms-2" /> إضافة وحدة
          </button>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label">بحث</label>
                <div className="input-group">
                  <input className="form-control" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="اسم/نوع الوحدة" />
                  <span className="input-group-text bg-light"><FaSearch /></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-lg border-0">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">جاري تحميل الوحدات...</p>
              </div>
            ) : filteredUnits?.length === 0 ? (
              <div className="text-center py-5 text-muted">لا توجد وحدات</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-modern">
                  <thead>
                    <tr>
                      <th className="text-center">كود الوحدة</th>
                      <th className="text-center">الاسم</th>
                      <th className="text-center">النوع</th>
                      <th className="text-center">الفئة</th>
                      <th className="text-center">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUnits.map((u, idx) => (
                      <tr key={u.id || idx}>
                       
                        <td className="text-center">
                          <div className="order-number-badge">#{u.id ?? '-'}</div>
                        </td>
                        <td className="text-center">{u.name || '-'}</td>
                        <td className="text-center"><span className="badge bg-light text-dark">{u.type || '-'}</span></td>
                        <td className="text-center">
                          <span className="d-inline-flex align-items-center gap-1">
                            <FaTags />
                            {
                              (u && typeof u.category_name === 'object')
                                ? (u.category_name.ar || u.category_name.en || '-')
                                : (u?.category_name || '-')
                            }
                          </span>
                        </td>
                        <td className="text-center" style={{ overflow: 'visible' }}>
                          <div className="units-action-buttons">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openEdit(u)}
                          >
                            <FaEdit className="ms-1" /> تعديل
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteUnit(u)}
                            disabled={deletingId === u.id}
                          >
                            <FaTrash className="ms-1" /> {deletingId === u.id ? 'جار الحذف...' : 'حذف'}
                          </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {addModalOpen && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px' }}>
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0">إضافة وحدة</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setAddModalOpen(false)}></button>
              </div>
              <form onSubmit={handleSubmitAdd}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">الاسم</label>
                    <input className="form-control" value={form.name} onChange={(e)=>setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">النوع</label>
                    <select className="form-select" value={form.type} onChange={(e)=>setForm({ ...form, type: e.target.value })} required>
                      <option value="">اختر النوع</option>
                      {UNIT_TYPES.map(t => (<option key={t} value={t}>{t}</option>))}
                    </select>
                  </div>
                  <div className="mb-0">
                    <label className="form-label">الفئة</label>
                    <select className="form-select" value={form.category_id} onChange={(e)=>setForm({ ...form, category_id: e.target.value })} required>
                      <option value="">اختر الفئة</option>
                      {categories.map(c => {
                        const label = (typeof c.title === 'object')
                          ? (c.title.ar || c.title.en)
                          : (c.title || ((typeof c.name === 'object') ? (c.name.ar || c.name.en) : c.name));
                        return (
                          <option key={c.id} value={c.id}>{label || `#${c.id}`}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={()=>setAddModalOpen(false)}>إلغاء</button>
                  <button type="submit" className="btn text-white" style={{ backgroundColor: 'var(--main-color)' }}>حفظ</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Styles to prevent button clipping */}
      <style>{`
        .units-action-buttons {
          display: inline-flex;
          gap: 0.5rem;
          align-items: center;
          justify-content: center;
          overflow: visible !important;
          white-space: nowrap !important;
        }
        .units-action-buttons .btn {
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
          padding: 0.4rem 0.9rem;
          width: auto !important;
          min-width: unset !important;
          max-width: none !important;
        }
        .table.table-modern td {
          overflow: visible !important;
        }
      `}</style>

      {editModalOpen && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '520px' }}>
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0">تعديل وحدة</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setEditModalOpen(false)}></button>
              </div>
              <form onSubmit={handleSubmitEdit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">الاسم</label>
                    <input className="form-control" value={editForm.name} onChange={(e)=>setEditForm({ ...editForm, name: e.target.value })} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">النوع</label>
                    <select className="form-select" value={editForm.type} onChange={(e)=>setEditForm({ ...editForm, type: e.target.value })} required>
                      <option value="">اختر النوع</option>
                      {UNIT_TYPES.map(t => (<option key={t} value={t}>{t}</option>))}
                    </select>
                  </div>
                  <div className="mb-0">
                    <label className="form-label">الفئة</label>
                    <select className="form-select" value={editForm.category_id} onChange={(e)=>setEditForm({ ...editForm, category_id: e.target.value })} required>
                      <option value="">اختر الفئة</option>
                      {categories.map(c => {
                        const label = (typeof c.title === 'object')
                          ? (c.title.ar || c.title.en)
                          : (c.title || ((typeof c.name === 'object') ? (c.name.ar || c.name.en) : c.name));
                        return (
                          <option key={c.id} value={c.id}>{label || `#${c.id}`}</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={()=>setEditModalOpen(false)}>إلغاء</button>
                  <button type="submit" className="btn text-white" style={{ backgroundColor: 'var(--main-color)' }} disabled={savingEdit}>
                    {savingEdit ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
            <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0">تأكيد الحذف</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setDeleteModalOpen(false)}></button>
              </div>
              <div className="modal-body p-4 text-center">
                <p className="mb-2">هل تريد حذف هذه الوحدة؟</p>
                <div className="text-muted">
                  {deleteTarget?.name ? `(${deleteTarget.name})` : ''}
                </div>
              </div>
              <div className="modal-footer justify-content-center">
                <button type="button" className="btn btn-secondary" onClick={()=>setDeleteModalOpen(false)}>إلغاء</button>
                <button type="button" className="btn btn-danger" onClick={confirmDeleteUnit} disabled={deletingId === deleteTarget?.id}>
                  {deletingId === deleteTarget?.id ? 'جار الحذف...' : 'حذف'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUnits;


